/**
 * Lesson Management Utilities
 * Provides high-level functions for managing lessons and slides
 */

import {
  getLesson,
  getLessonSlides,
  createSlide,
  updateSlide,
  deleteSlide,
  updateLesson,
  reorderSlides,
  deleteLesson as deleteBasicLesson,
  deleteLessonShares
} from '../../utils/supabase/queries';
import type { Lesson, Slide } from '../../utils/supabase/client';
import { supabase } from '../../utils/supabase/client';
import { deleteLessonCover } from '../../utils/supabase/storage';
import { getDefaultContentForSlideType } from '../slides/slideRegistry';

export interface LessonWithSlides extends Lesson {
  slides: Slide[];
}

// ============================================================
// LESSON DELETION WITH VALIDATION
// ============================================================

export interface DeleteLessonResult {
  success: boolean;
  error?: string;
  hasActiveSessions?: boolean;
}

/**
 * Check if a lesson has any active sessions
 */
export async function hasActiveSessions(lessonId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('sessions')
    .select('id, status')
    .eq('lesson_id', lessonId)
    .eq('status', 'active')
    .limit(1);

  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/**
 * Delete a lesson with comprehensive validation and cleanup
 * 
 * This function:
 * 1. Validates lesson ownership (via RLS)
 * 2. Checks for active sessions
 * 3. Deletes cover image from storage
 * 4. Deletes lesson (cascade deletes all related records)
 * 
 * Database CASCADE will automatically delete:
 * - slides (via slides.lesson_id FK)
 * - sessions (via sessions.lesson_id FK)
 * - session_participants (via sessions FK cascade)
 * - submissions (via sessions FK cascade)
 * - grades (via grades.lesson_id FK)
 * 
 * @param lessonId - The ID of the lesson to delete
 * @returns DeleteLessonResult with success status and optional error
 */
export async function deleteLessonWithValidation(lessonId: string): Promise<DeleteLessonResult> {
  try {
    // 1. Get lesson details to check cover image
    const lesson = await getLesson(lessonId);
    
    // 2. Check for active sessions
    const hasActive = await hasActiveSessions(lessonId);
    if (hasActive) {
      return {
        success: false,
        hasActiveSessions: true,
        error: 'Cannot delete a lesson with active sessions. Please end all sessions first.'
      };
    }

    // 3. Delete cover image from storage if it exists
    if (lesson.cover_image) {
      try {
        await deleteLessonCover(lesson.cover_image);
      } catch (storageError) {
        // Log but don't fail the deletion if storage cleanup fails
        console.warn('Failed to delete cover image from storage:', storageError);
      }
    }

    // 4. Remove shared lesson entries before deleting the lesson
    await deleteLessonShares(lessonId);

    // 5. Explicitly clean up grade_summary rows (including those created
    //    by shared teachers) while the lessons row still exists so the
    //    RLS policy can verify lesson ownership.
    try {
      await supabase
        .from('grade_summary')
        .delete()
        .eq('lesson_id', lessonId);
    } catch (gsError) {
      console.warn('Failed to pre-clean grade_summary (will rely on CASCADE):', gsError);
    }

    // 6. Delete the lesson (CASCADE will handle all remaining related records)
    await deleteBasicLesson(lessonId);

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting lesson:', error);
    
    // Handle specific error cases
    if (error?.code === 'PGRST116' || error?.message?.includes('not found')) {
      return {
        success: false,
        error: 'Lesson not found or you do not have permission to delete it.'
      };
    }
    
    if (error?.code === '23503') {
      return {
        success: false,
        error: 'Cannot delete lesson due to existing dependencies. Please contact support.'
      };
    }

    return {
      success: false,
      error: error?.message || 'Failed to delete lesson. Please try again.'
    };
  }
}

/**
 * Load a complete lesson with all its slides
 */
export async function loadLessonWithSlides(lessonId: string): Promise<LessonWithSlides> {
  const lesson = await getLesson(lessonId);
  const slides = await getLessonSlides(lessonId);
  
  return {
    ...lesson,
    slides
  };
}

/**
 * Add a new slide to a lesson
 */
export async function addSlideToLesson(
  lessonId: string,
  slideType: Slide['type'],
  title: string
): Promise<Slide> {
  // Get current slides to determine order
  const existingSlides = await getLessonSlides(lessonId);
  const nextOrder = existingSlides.length;

  // Create default content based on type
  const defaultContent = getDefaultContentForType(slideType);

  const newSlide = await createSlide({
    lesson_id: lessonId,
    type: slideType,
    content: {
      title,
      ...defaultContent
    },
    slide_order: nextOrder
  });

  return newSlide;
}

/**
 * Get default content structure for each slide type
 * Delegates to the slide registry as single source of truth
 */
function getDefaultContentForType(type: Slide['type']): Record<string, any> {
  return getDefaultContentForSlideType(type);
}

/**
 * Update slide content
 */
export async function updateSlideContent(
  slideId: string,
  content: Record<string, any>
): Promise<Slide> {
  return updateSlide(slideId, { content });
}

/**
 * Delete a slide and reorder remaining slides
 */
export async function deleteSlideAndReorder(
  lessonId: string,
  slideId: string
): Promise<void> {
  // Delete the slide
  await deleteSlide(slideId);
  
  // Get remaining slides
  const remainingSlides = await getLessonSlides(lessonId);
  
  // Reorder slides
  const reorderUpdates = remainingSlides.map((slide, index) => ({
    id: slide.id,
    slide_order: index
  }));
  
  if (reorderUpdates.length > 0) {
    await reorderSlides(reorderUpdates);
  }
}

/**
 * Duplicate a slide
 */
export async function duplicateSlide(slideId: string): Promise<Slide> {
  const originalSlide = await supabase
    .from('slides')
    .select('*')
    .eq('id', slideId)
    .single();
  
  if (originalSlide.error) throw originalSlide.error;
  
  const existingSlides = await getLessonSlides(originalSlide.data.lesson_id);
  const nextOrder = existingSlides.length;
  
  const newSlide = await createSlide({
    lesson_id: originalSlide.data.lesson_id,
    type: originalSlide.data.type,
    content: {
      ...originalSlide.data.content,
      title: `${originalSlide.data.content.title} (Copy)`
    },
    slide_order: nextOrder
  });
  
  return newSlide;
}

/**
 * Move a slide to a new position
 */
export async function moveSlide(
  lessonId: string,
  slideId: string,
  newPosition: number
): Promise<void> {
  const slides = await getLessonSlides(lessonId);
  
  // Find the slide to move
  const slideIndex = slides.findIndex(s => s.id === slideId);
  if (slideIndex === -1) return;
  
  // Remove slide from current position
  const [slide] = slides.splice(slideIndex, 1);
  
  // Insert at new position
  slides.splice(newPosition, 0, slide);
  
  // Update all slide orders
  const reorderUpdates = slides.map((s, index) => ({
    id: s.id,
    slide_order: index
  }));
  
  await reorderSlides(reorderUpdates);
}

/**
 * Publish a lesson (change status to published)
 */
export async function publishLesson(lessonId: string): Promise<Lesson> {
  return updateLesson(lessonId, { status: 'published' });
}

/**
 * Unpublish a lesson (change status to draft)
 */
export async function unpublishLesson(lessonId: string): Promise<Lesson> {
  return updateLesson(lessonId, { status: 'draft' });
}

/**
 * Update lesson metadata
 */
export async function updateLessonMetadata(
  lessonId: string,
  data: {
    title?: string;
    description?: string;
    mode?: 'live' | 'student-paced';
    cover_image?: string | null;
  }
): Promise<Lesson> {
  return updateLesson(lessonId, data);
}